import React, { useState, useEffect, useCallback, useMemo } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import {
  LinearProgress,
  TableContainer,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import NewLeads from './new-leads'
import ContactedLeads from './contacted-leads'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import TablePagination from 'components/pagination/table-pagination'
import {
  ALL_INTEREST_FILTER,
  getLeadInterestFilterOptions,
  leadMatchesInterestFilter,
  partitionLeadsByContactStatus,
} from './utils'

export const useStyles = makeStyles(theme => ({
  leadNameCell: {
    paddingLeft: '1.5rem',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 1, 2),
    },
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  filterButton: {
    height: 40,
    borderRadius: 8,
    fontWeight: 500,
    flexShrink: 0,
    maxWidth: 280,
    textTransform: 'none',
    borderColor: '#C4C7C9',
    color: '#172024',
    '& .MuiButton-endIcon': {
      color: '#6B7C87',
    },
    '& .MuiButton-label': {
      overflow: 'hidden',
    },
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    fontWeight: 600,
    '& .MuiButton-endIcon': {
      color: '#001C57',
    },
    '&:hover': {
      backgroundColor: '#D4E2FF',
      borderColor: theme.palette.primary.main,
    },
  },
  filterButtonText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filterMenuPaper: {
    marginTop: theme.spacing(0.5),
    minWidth: 220,
    maxWidth: 360,
    maxHeight: 400,
  },
  filterMenuContent: {
    padding: theme.spacing(1.5, 2),
    boxSizing: 'border-box',
  },
  filterForm: {
    margin: 0,
    padding: 0,
    border: 0,
    minWidth: 0,
  },
  filterLegend: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#6B7C87',
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  filterRadioGroup: {
    padding: 0,
    margin: 0,
  },
  filterRadioLabel: {
    margin: 0,
    width: '100%',
    minHeight: 40,
    marginLeft: 0,
    marginRight: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      color: '#172024',
    },
    '& .MuiRadio-root': {
      color: '#40484C',
      padding: theme.spacing(1),
    },
    '& .MuiRadio-colorPrimary.Mui-checked': {
      color: '#0053FF',
    },
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  coachPlusSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  coachPlusCopy: {
    flex: 1,
    minWidth: 0,
  },
  coachPlusTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
    marginBottom: theme.spacing(0.75),
  },
  coachPlusCaption: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  coachPlusButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1.25, 3.5),
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
  },
  coachPlusBanner: {
    marginBottom: theme.spacing(2),
  },
}))

const LeadsTable = ({
  searchableItems = [],
  coachingLeadContactMethodOptions = [],
  refetch,
  freeTierLimitsReached,
  tab,
}) => {
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedInterest, setSelectedInterest] = useState(ALL_INTEREST_FILTER)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25
  const isLeadsLimitReached = (freeTierLimitsReached ?? []).includes('LEADS')
  const tabLeads = useMemo(() => {
    const {
      awaitingResponseLeads,
      waitlistedLeads,
      contactedLeads,
    } = partitionLeadsByContactStatus(searchableItems)
    if (tab === 'waitlisted') return waitlistedLeads
    if (tab === 'contacted') return contactedLeads
    return awaitingResponseLeads
  }, [searchableItems, tab])
  const interestOptions = useMemo(
    () => getLeadInterestFilterOptions(tabLeads),
    [tabLeads],
  )
  const showInterestFilter = interestOptions.length > 2

  const {
    upgradeLoading: loading,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('leads')

  const nameMatchesSearchToken = useCallback(
    ({ firstName = '', lastName = '' }, token) => {
      const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
      const term = token.toLowerCase().trim()

      return fullName.includes(term)
    },
    [],
  )

  const currentLeads = useMemo(
    () =>
      tabLeads.filter(
        item =>
          nameMatchesSearchToken(item, searchTerm) &&
          leadMatchesInterestFilter(item, selectedInterest),
      ),
    [nameMatchesSearchToken, tabLeads, searchTerm, selectedInterest],
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm, selectedInterest, tab])

  useEffect(() => {
    if (
      selectedInterest !== ALL_INTEREST_FILTER &&
      !interestOptions.some(option => option.value === selectedInterest)
    ) {
      setSelectedInterest(ALL_INTEREST_FILTER)
    }
  }, [interestOptions, selectedInterest])

  const sortByCreatedOn = useCallback((a, b, dir) => {
    a = a.props.lead.createdAt
    b = b.props.lead.createdAt

    if (a < b) return dir === 'desc' ? 1 : -1
    if (a === b) return 0
    if (a > b) return dir === 'desc' ? -1 : 1
    return 0
  }, [])

  const getLeadSortName = useCallback(lead => {
    const firstName = lead?.firstName ?? lead?.contact?.firstName ?? ''
    const lastName = lead?.lastName ?? lead?.contact?.lastName ?? ''
    return `${firstName}${lastName}`.toLowerCase().replace(/\s/g, '')
  }, [])

  const sortByFullName = useCallback(
    (a, b, dir) => {
      a = getLeadSortName(a.props.lead)
      b = getLeadSortName(b.props.lead)

      if (a > b) return dir === 'desc' ? 1 : -1
      if (a < b) return dir === 'desc' ? -1 : 1
      return 0
    },
    [getLeadSortName],
  )

  const pageCount = Math.ceil((currentLeads?.length || 0) / rowsPerPage) || 1
  const filterMenuOpen = Boolean(filterAnchorEl)
  const hasActiveInterestFilter = selectedInterest !== ALL_INTEREST_FILTER
  const selectedInterestLabel =
    interestOptions.find(option => option.value === selectedInterest)?.label ||
    'Filters'
  const emptyCaption = useMemo(() => {
    if (tabLeads.length > 0 && currentLeads.length === 0) {
      return 'No matching leads.'
    }
    if (tab === 'waitlisted') return 'No waitlisted leads.'
    if (tab === 'contacted') return 'No contacted leads.'
    return 'No leads awaiting response.'
  }, [currentLeads.length, tabLeads.length, tab])

  return (
    <>
      <TableContainer className={classes.containerStyles}>
        {loading && <LinearProgress color="primary" />}
        {isLeadsLimitReached && (
          <Box className={classes.coachPlusBanner}>
            <Box className={classes.coachPlusSection}>
              <Box className={classes.coachPlusCopy}>
                <Typography className={classes.coachPlusTitle}>
                  Unlock Unlimited Leads
                </Typography>
                <Typography className={classes.coachPlusCaption}>
                  Enjoy up to two free leads. Upgrade to PGA Coach+ for
                  unlimited coaching requests, featured PGA.com placement,
                  premium profile tools and business insights.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                className={classes.coachPlusButton}
                onClick={handleUpgradeClick}
                startIcon={<OpenInNewOutlinedIcon />}
              >
                Upgrade Now
              </Button>
            </Box>
          </Box>
        )}
        <Box className={classes.tableShell}>
          <Box className={classes.toolbar}>
            <Box className={classes.searchGroup}>
              <SearchBar
                className={classes.searchField}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search Leads"
                data-testid="leads-list-search"
                style={{ margin: 0 }}
              />
              {showInterestFilter && (
                <>
                  <Button
                    variant="outlined"
                    color="default"
                    className={`${classes.filterButton} ${
                      hasActiveInterestFilter ? classes.filterButtonActive : ''
                    }`}
                    aria-controls="lead-interest-filters-menu"
                    aria-haspopup="true"
                    aria-expanded={filterMenuOpen}
                    aria-label={
                      hasActiveInterestFilter
                        ? `Lead interest filter: ${selectedInterestLabel} selected`
                        : 'Lead interest filter'
                    }
                    data-testid="leads-interest-filter"
                    onClick={event => setFilterAnchorEl(event.currentTarget)}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    <span className={classes.filterButtonText}>
                      {hasActiveInterestFilter
                        ? selectedInterestLabel
                        : 'Filters'}
                    </span>
                  </Button>
                  <Popover
                    id="lead-interest-filters-menu"
                    open={filterMenuOpen}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      className: classes.filterMenuPaper,
                      style: filterAnchorEl
                        ? { minWidth: filterAnchorEl.clientWidth }
                        : undefined,
                    }}
                  >
                    <Box className={classes.filterMenuContent}>
                      <FormControl
                        component="fieldset"
                        fullWidth
                        className={classes.filterForm}
                      >
                        <FormLabel
                          component="legend"
                          className={classes.filterLegend}
                        >
                          Interest
                        </FormLabel>
                        <RadioGroup
                          aria-label="Interest filter"
                          name="lead-interest-filter"
                          className={classes.filterRadioGroup}
                          value={selectedInterest}
                          onChange={event => {
                            setSelectedInterest(event.target.value)
                            setFilterAnchorEl(null)
                          }}
                        >
                          {interestOptions.map(option => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              className={classes.filterRadioLabel}
                              control={<Radio color="primary" size="small" />}
                              label={option.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Popover>
                </>
              )}
            </Box>
            <Box className={classes.toolbarActions}>
              {currentLeads.length > 0 && (
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  rowsPerPage={rowsPerPage}
                  totalCount={currentLeads.length}
                  onPageChange={setPage}
                  alwaysShowCount
                />
              )}
            </Box>
          </Box>
          <Box className={classes.tableContent}>
            {tab === 'contacted' ? (
              <ContactedLeads
                contactedLeads={currentLeads}
                refetch={refetch}
                sortByFullName={sortByFullName}
                sortByCreatedOn={sortByCreatedOn}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                emptyCaption={emptyCaption}
              />
            ) : (
              <NewLeads
                awaitingResponseLeads={currentLeads}
                contactMethodOptions={coachingLeadContactMethodOptions}
                refetch={refetch}
                sortByFullName={sortByFullName}
                sortByCreatedOn={sortByCreatedOn}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                emptyCaption={emptyCaption}
                label={
                  tab === 'waitlisted'
                    ? 'Waitlisted Leads'
                    : 'Awaiting Response'
                }
              />
            )}
          </Box>
        </Box>
      </TableContainer>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default LeadsTable
